/**
 * The ACTIVE Classroom courses the registrar's own grant returns. A course an
 * IMAM section already links shows that section instead of an intake, and,
 * when the Google grant that reads the link has ended, offers to take its
 * reads over with the registrar's own connection (contract v4 D6).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { toast } from "sonner";

import { DataWorkspace, WorkspaceState } from "@/components/records/AcademicWorkspace";
import { Button } from "@/components/ui/button";
import {
  CLASSROOM_INTAKE_QUERY_KEY,
  classroomIntakeApi,
  classroomIntakeError,
  classroomIntakeNeedsConnection,
  type ClassroomIntakeCourse,
  type ClassroomIntakeLinkedSection,
} from "@/lib/api/classroomIntake";
import { registrarCoursePath } from "@/navigation/routes";

const linkClass = "font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function IntakeCourseList({
  courses,
  loading,
  error,
  onRetry,
  onReview,
}: {
  courses: ClassroomIntakeCourse[] | undefined;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onReview: (providerCourseId: string) => void;
}) {
  return (
    <DataWorkspace
      title="Classroom courses"
      description="Active courses your Google account can see. Review one to bring it into IMAM."
    >
      {loading && <WorkspaceState kind="loading" description="Reading your Classroom courses" />}
      {error && <WorkspaceState kind="error" title="Classroom courses could not be read" description={error} onRetry={onRetry} />}
      {courses && courses.length === 0 && (
        <WorkspaceState kind="empty" description="Your Google account sees no active Classroom courses." />
      )}
      {courses && courses.length > 0 && (
        <ul aria-label="Classroom courses" className="grid min-w-0">
          {courses.map((course) => (
            <li
              key={course.provider_course_id}
              aria-label={course.name}
              className="grid min-w-0 gap-2 border-b border-rule py-3 ps-5 pe-4 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-4"
            >
              <div className="grid min-w-0 gap-0.5 [overflow-wrap:anywhere]">
                <span className="text-ui font-medium text-foreground">{course.name}</span>
                <span className="text-meta text-muted-foreground">
                  {[course.section, course.owner?.name ? `Owner ${course.owner.name}` : null].filter(Boolean).join(" · ")}
                </span>
                {course.linked_section && (
                  <span className="text-meta text-foreground">
                    In IMAM as{" "}
                    <Link to={registrarCoursePath(course.linked_section.course_id)} className={linkClass}>
                      <span className="font-mono">{course.linked_section.code}</span> {course.linked_section.title}
                    </Link>
                    {course.linked_section.term_name && <span className="text-muted-foreground"> · {course.linked_section.term_name}</span>}
                  </span>
                )}
                {course.linked_section?.custody === "lost" && <LostCustody link={course.linked_section} />}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!course.linked_section && (
                  <Button type="button" size="sm" variant="outline" aria-label={`Review ${course.name}`} onClick={() => onReview(course.provider_course_id)}>
                    Review
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </DataWorkspace>
  );
}

/** Retained provider-management surface, outside the mapped intake renewal. */
export function LostCustody({ link }: { link: ClassroomIntakeLinkedSection }) {
  const queryClient = useQueryClient();
  const takeOver = useMutation({
    mutationFn: () => classroomIntakeApi.takeOverCustody(link.course_id),
    onSuccess: () => {
      toast.success(`${link.code} now reads Classroom with your connection`);
      void queryClient.invalidateQueries({ queryKey: [...CLASSROOM_INTAKE_QUERY_KEY, "courses"] });
    },
    onError: (error) => {
      if (classroomIntakeNeedsConnection(error)) {
        void queryClient.invalidateQueries({ queryKey: [...CLASSROOM_INTAKE_QUERY_KEY, "connection"] });
      }
    },
  });
  if (takeOver.isSuccess) return null;
  return (
    <div className="grid justify-items-start gap-1.5 pt-1">
      <p className="text-meta text-muted-foreground">The Google connection that read this section has ended.</p>
      {takeOver.isError && (
        <p role="alert" className="text-meta text-destructive">
          {classroomIntakeError(takeOver.error)?.message || "Reading could not be taken over. Try again."}
        </p>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        aria-label={`Use my Classroom connection for ${link.code}`}
        disabled={takeOver.isPending}
        onClick={() => takeOver.mutate()}
      >
        {takeOver.isPending ? "Connecting…" : "Use my Classroom connection"}
      </Button>
    </div>
  );
}
